import { clstx } from '@payaca/utilities/cvaMerge';
import { ComponentPropsWithoutRef, forwardRef } from 'react';

export type ChartTooltipKey = {
  label: string;
  value: string;
  colour: string;
  striped?: boolean;
  outlined?: boolean;
};

const stripedSwatch = (colour: string) => ({
  border: '1px solid #e0e0e0',
  backgroundImage: `repeating-linear-gradient(135deg, color-mix(in srgb, ${colour} 50%, transparent) 0 1px, transparent 1px 4.25px)`,
});

const swatchStyle = (key: ChartTooltipKey) => {
  if (key.outlined) {
    return { border: `1px solid ${key.colour}` };
  }

  return key.striped
    ? stripedSwatch(key.colour)
    : { backgroundColor: key.colour };
};

interface IProps extends ComponentPropsWithoutRef<'div'> {
  title: string;
  keys: ChartTooltipKey[];
}

const ChartTooltip = forwardRef<HTMLDivElement, IProps>((props, ref) => {
  const { title, keys, className, ...rest } = props;

  return (
    <div
      className={clstx(
        'typography:prose bg-white text-sm text-blue-900',
        'overflow-hidden rounded-lg border border-gray-200 shadow',
        className
      )}
      ref={ref}
      {...rest}
    >
      <div className="bg-gray-50 px-3.5 py-2 font-semibold">{title}</div>

      <div className="px-3.5 py-2">
        <table>
          <tbody>
            {keys.map((key) => (
              <tr key={key.label}>
                <td className="pr-2">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={swatchStyle(key)}
                  />
                </td>
                <td className="pr-2">{key.label}</td>
                <td className="text-right">{key.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default ChartTooltip;
